import {
  BoxGeometry,
  ConeGeometry,
  CylinderGeometry,
  DoubleSide,
  Group,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  RingGeometry,
  SphereGeometry,
  TorusGeometry
} from 'three';
import { ChampionMeshRegistry } from './championMeshRegistry.js';

export class ChampionMeshFactory {
  /**
   * Factory dispatcher — OCP via ChampionMeshRegistry (no champ if/else).
   */
  static createChampionMesh(championKey = 'ashe', team = 'blue') {
    return ChampionMeshRegistry.create(championKey, team)
      || ChampionMeshFactory.createAsheMesh(team);
  }
  /**
   * Creates 3D mesh for Classic Ashe (Frost Archer with navy/gold cloak, white hair & icy bow).
   */
  static createAsheMesh(team = 'blue') {
    const group = new Group();
    const cloakColor = team === 'blue' ? 0x1e3a8a : 0x991b1b;
    const goldTrim = 0xf59e0b;
    const iceColor = 0x38bdf8;
    const hairColor = 0xf8fafc;
    const skinColor = 0xffedd5;

    const cloakMat = new MeshStandardMaterial({ color: cloakColor, roughness: 0.5 });
    const trimMat = new MeshStandardMaterial({ color: goldTrim, metalness: 0.6, roughness: 0.3 });
    const skinMat = new MeshStandardMaterial({ color: skinColor, roughness: 0.7 });
    const hairMat = new MeshStandardMaterial({ color: hairColor, roughness: 0.4 });
    const bowMat = new MeshStandardMaterial({ color: iceColor, emissive: iceColor, emissiveIntensity: 0.6, roughness: 0.1 });
    const stringMat = new MeshBasicMaterial({ color: 0xbae6fd });

    // Head with White/Silver Hair
    const head = new Mesh(new SphereGeometry(0.48, 16, 16), skinMat);
    head.position.y = 3.3;
    group.add(head);

    const hair = new Mesh(new ConeGeometry(0.55, 0.9, 12), hairMat);
    hair.position.set(0, 3.5, -0.1);
    hair.rotation.x = -0.2;
    group.add(hair);

    const hood = new Mesh(new ConeGeometry(0.62, 0.9, 12), cloakMat);
    hood.position.y = 3.65;
    hood.rotation.x = -0.2;
    group.add(hood);

    // Torso with Gold Trim Collar
    const torso = new Mesh(new CylinderGeometry(0.65, 0.45, 2.1, 12), cloakMat);
    torso.position.y = 1.95;
    group.add(torso);

    const collar = new Mesh(new TorusGeometry(0.55, 0.08, 8, 16), trimMat);
    collar.rotation.x = Math.PI / 2;
    collar.position.y = 2.9;
    group.add(collar);

    // Quiver with Frost Arrows on Back
    const quiver = new Mesh(new CylinderGeometry(0.18, 0.14, 1.4, 8), trimMat);
    quiver.rotation.z = Math.PI / 6;
    quiver.position.set(0.35, 2.3, -0.4);
    group.add(quiver);

    // Frost Bow (Left Arm)
    const bowGroup = new Group();
    const upperLimb = new Mesh(new CylinderGeometry(0.06, 0.03, 1.4, 8), bowMat);
    upperLimb.position.set(0, 0.6, 0);
    upperLimb.rotation.z = Math.PI / 6;
    bowGroup.add(upperLimb);

    const lowerLimb = new Mesh(new CylinderGeometry(0.03, 0.06, 1.4, 8), bowMat);
    lowerLimb.position.set(0, -0.6, 0);
    lowerLimb.rotation.z = -Math.PI / 6;
    bowGroup.add(lowerLimb);

    // Bow String
    const string = new Mesh(new CylinderGeometry(0.015, 0.015, 2.4, 4), stringMat);
    string.position.set(-0.25, 0, 0);
    bowGroup.add(string);

    bowGroup.position.set(-0.7, 2.1, 0.3);
    group.add(bowGroup);

    // Procedural Animation Update
    group.userData.update = (dt, elapsed = 0) => {
      bowGroup.position.y = 2.1 + Math.sin(elapsed * 2) * 0.05;
      bowMat.emissiveIntensity = 0.5 + 0.3 * Math.sin(elapsed * 3);
    };

    return group;
  }

  /**
   * Creates 3D mesh for Classic Garen (Might of Demacia with silver steel armor, gold pauldrons & broadsword).
   */
  static createGarenMesh(team = 'red') {
    const group = new Group();
    const teamColor = team === 'blue' ? 0x1d4ed8 : 0xb91c1c;

    const armorMat = new MeshStandardMaterial({ color: 0xcbd5e1, metalness: 0.85, roughness: 0.15 });
    const trimMat = new MeshStandardMaterial({ color: 0xf59e0b, metalness: 0.7, roughness: 0.25 });
    const capeMat = new MeshStandardMaterial({ color: teamColor, roughness: 0.5 });

    // Heavy Torso Armor
    const torso = new Mesh(new CylinderGeometry(0.85, 0.65, 2.4, 12), armorMat);
    torso.position.y = 2.1;
    group.add(torso);

    // Demacian Crest on Chest
    const crest = new Mesh(new BoxGeometry(0.35, 0.45, 0.1), trimMat);
    crest.position.set(0, 2.4, 0.45);
    group.add(crest);

    // Head & Helmet
    const head = new Mesh(new SphereGeometry(0.52, 16, 16), armorMat);
    head.position.y = 3.5;
    group.add(head);

    // Massive Golden Pauldrons
    const leftPauldron = new Mesh(new BoxGeometry(0.8, 0.6, 0.9), trimMat);
    leftPauldron.position.set(-1.0, 3.1, 0);
    group.add(leftPauldron);

    const rightPauldron = new Mesh(new BoxGeometry(0.8, 0.6, 0.9), trimMat);
    rightPauldron.position.set(1.0, 3.1, 0);
    group.add(rightPauldron);

    // Broadsword of Demacia
    const swordGroup = new Group();
    const hilt = new Mesh(new BoxGeometry(0.7, 0.12, 0.18), trimMat);
    hilt.position.set(0, 0, 0);

    const blade = new Mesh(new BoxGeometry(0.24, 2.4, 0.08), armorMat);
    blade.position.set(0, 1.25, 0);

    swordGroup.add(hilt);
    swordGroup.add(blade);
    swordGroup.position.set(0.85, 1.8, 0.5);
    group.add(swordGroup);

    // Royal Cape
    const cape = new Mesh(new BoxGeometry(1.7, 2.4, 0.1), capeMat);
    cape.position.set(0, 2.0, -0.48);
    group.add(cape);

    group.userData.update = (dt, elapsed = 0) => {
      cape.rotation.x = Math.sin(elapsed * 2.5) * 0.06;
      swordGroup.rotation.z = Math.sin(elapsed * 1.8) * 0.05;
    };

    return group;
  }

  /**
   * Creates 3D mesh for Classic Ryze (Rune Mage with arcane blue skin, glowing tattoos & giant scroll).
   */
  static createRyzeMesh(team = 'blue') {
    const group = new Group();
    const skinMat = new MeshStandardMaterial({ color: 0x3b82f6, emissive: 0x1d4ed8, emissiveIntensity: 0.3, roughness: 0.4 });
    const tattooMat = new MeshBasicMaterial({ color: 0x93c5fd });
    const robeMat = new MeshStandardMaterial({ color: 0x1e1b4b, roughness: 0.6 });
    const scrollMat = new MeshStandardMaterial({ color: 0x78350f, roughness: 0.7 });
    const bandMat = new MeshStandardMaterial({ color: 0xf59e0b, metalness: 0.6 });

    // Robe Torso
    const torso = new Mesh(new CylinderGeometry(0.65, 0.5, 2.2, 12), robeMat);
    torso.position.y = 2.0;
    group.add(torso);

    // Arcane Blue Head
    const head = new Mesh(new SphereGeometry(0.5, 16, 16), skinMat);
    head.position.y = 3.35;
    group.add(head);

    // Glowing Arcane Tattoo Ring around head/chest
    const tattooRing = new Mesh(new TorusGeometry(0.48, 0.04, 8, 16), tattooMat);
    tattooRing.rotation.x = Math.PI / 2;
    tattooRing.position.y = 3.35;
    group.add(tattooRing);

    // Giant Rune Scroll (Strapped to Back)
    const scrollGroup = new Group();
    const scrollBody = new Mesh(new CylinderGeometry(0.42, 0.42, 3.0, 12), scrollMat);
    scrollGroup.add(scrollBody);

    const bandTop = new Mesh(new TorusGeometry(0.44, 0.05, 8, 16), bandMat);
    bandTop.rotation.x = Math.PI / 2;
    bandTop.position.y = 1.0;
    scrollGroup.add(bandTop);

    const bandBottom = new Mesh(new TorusGeometry(0.44, 0.05, 8, 16), bandMat);
    bandBottom.rotation.x = Math.PI / 2;
    bandBottom.position.y = -1.0;
    scrollGroup.add(bandBottom);

    scrollGroup.rotation.z = Math.PI / 4;
    scrollGroup.position.set(0, 2.5, -0.52);
    group.add(scrollGroup);

    group.userData.update = (dt, elapsed = 0) => {
      skinMat.emissiveIntensity = 0.3 + 0.2 * Math.sin(elapsed * 3);
      scrollGroup.rotation.y = Math.sin(elapsed * 1.5) * 0.08;
    };

    return group;
  }

  /**
   * Creates 3D mesh for Training Dummy.
   */
  static createDummyMesh() {
    const group = new Group();
    const dummyMat = new MeshStandardMaterial({ color: 0x64748b, roughness: 0.6 });

    const body = new Mesh(new CylinderGeometry(0.8, 0.6, 2.8, 12), dummyMat);
    body.position.y = 1.4;
    group.add(body);

    const head = new Mesh(new SphereGeometry(0.45, 12, 12), dummyMat);
    head.position.y = 3.15;
    group.add(head);

    const baseRing = new Mesh(
      new RingGeometry(0.8, 1.0, 24).rotateX(-Math.PI / 2),
      new MeshBasicMaterial({ color: 0xfbbf24, side: DoubleSide })
    );
    baseRing.position.y = 0.05;
    group.add(baseRing);

    return group;
  }
}

ChampionMeshRegistry.register('ashe', (team) => ChampionMeshFactory.createAsheMesh(team));
ChampionMeshRegistry.register('garen', (team) => ChampionMeshFactory.createGarenMesh(team));
ChampionMeshRegistry.register('ryze', (team) => ChampionMeshFactory.createRyzeMesh(team));
ChampionMeshRegistry.register('dummy', () => ChampionMeshFactory.createDummyMesh());
